
import { Component, Vue } from 'vue-property-decorator';
import { TreeNode, NodeType, mapLocationToTreeNode } from '@/modules/nomenclature/models/treeNode';
import * as LocationService from '@/modules/nomenclature/services/locationService';
import LocationForm from '@/modules/nomenclature/components/LocationForm.vue';
import DeleteLocationForm from '@/modules/nomenclature/components/DeleteLocationForm.vue';

@Component({
  components: { LocationForm, DeleteLocationForm }
})
export default class Locations extends Vue {
  protected loading = false;
  protected sites: TreeNode[] = [];
  protected openIds: string[] = [];
  protected static id = 1;

  protected created(): void {
    this.loading = true;
    LocationService.fetchAll()
      .then((response) => {
        // Root locations
        for (const location of response.data) {
          const locationNode = mapLocationToTreeNode(location, 0);
          this.sites.push(locationNode);
        }
        // Virtual node "add location"
        const virtualNode = this.getVirtualNodeAddLocation(null, 0);
        this.sites.push(virtualNode);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  protected async fetchLocationChildren(item: TreeNode): Promise<void> {
    const depth = item.depth !== undefined ? item.depth + 1 : -1;
    return LocationService.fetchChildren(item)
      .then((response) => {
        item.children = [];
        for (const child of response.data) {
          const locationNode = mapLocationToTreeNode(child, depth);
          item.children.push(locationNode);
        }
        const virtualNode = this.getVirtualNodeAddLocation(item.id as string, depth);
        item.children.push(virtualNode);
      })
      .catch();
  }

  protected createLocation(location: TreeNode): Promise<void> {
    return new Promise((resolve, reject) => {
      LocationService.create(location)
        .then((locationSaved) => {
          const locationNode = mapLocationToTreeNode(locationSaved.data, location.depth);
          this.insertIntoTree(locationNode, this.sites);
          resolve();
        })
        .catch((e) => reject(e));
    });
  }

  protected insertIntoTree(location: TreeNode, sites: TreeNode[]): void {
    location.children = [];

    if (!location.parentId) {
      location.depth = 0;
      this.sites.splice(this.sites.length - 1, 1, location);
      this.sites.push(this.getVirtualNodeAddLocation(null, 0));
    } else {
      sites.forEach((site) => {
        if (site.id === location.parentId) {
          if (!site.children) {
            site.children = [];
          }
          location.depth = site.depth !== undefined ? site.depth + 1 : -1;
          site.children.splice(site.children.length - 1, 1, location);
          site.children.push(this.getVirtualNodeAddLocation(site.id as string, location.depth));
          return;
        }
        if (site.children && site.children.length > 0) {
          this.insertIntoTree(location, site.children);
        }
      });
    }
  }

  protected updateLocation(location: TreeNode, oldLocation: TreeNode): Promise<void> {
    return new Promise((resolve, reject) => {
      LocationService.update(location)
        .then((locationUpdated) => {
          const locationNode = mapLocationToTreeNode(locationUpdated.data, location.depth);
          locationNode.children = location.children;
          this.updateIntoTree(oldLocation, this.sites, locationNode);
          resolve();
        })
        .catch((e) => reject(e));
    });
  }

  protected updateIntoTree(oldLocation: TreeNode, sites: TreeNode[], locationUpdated: TreeNode): void {
    if (!oldLocation.parentId) {
      this.sites.splice(this.sites.indexOf(oldLocation), 1, locationUpdated);
    } else {
      sites.forEach((site) => {
        if (site.children) {
          if (site.id === oldLocation.parentId) {
            site.children.splice(site.children.indexOf(oldLocation), 1, locationUpdated);
            return;
          }
          if (site.children.length > 0) {
            this.updateIntoTree(oldLocation, site.children, locationUpdated);
          }
        }
      });
    }
  }

  protected deleteLocation(locationId: string, onDeleteCascadeRcv: boolean, oldLocation: TreeNode): Promise<void> {
    const param = { onDeleteCascade: onDeleteCascadeRcv };
    return new Promise((resolve, reject) => {
      LocationService.destroy(locationId, param)
        .then(() => {
          resolve();
          this.deleteIntoTree(oldLocation, this.sites);
        })
        .catch((e) => reject(e));
    });
  }

  protected deleteIntoTree(oldLocation: TreeNode, sites: TreeNode[]): void {
    if (!oldLocation.parentId) {
      this.sites.splice(this.sites.indexOf(oldLocation), 1);
    } else {
      sites.forEach((site) => {
        if (site.children) {
          if (site.id === oldLocation.parentId) {
            site.children.splice(site.children.indexOf(oldLocation), 1);
            return;
          }
          if (site.children.length > 0) {
            this.deleteIntoTree(oldLocation, site.children);
          }
        }
      });
    }
  }

  protected getVirtualNodeAddLocation(parentId: string | null, depth: number): TreeNode {
    return {
      id: (Locations.id++).toString(),
      icon: this.$i18n.t('common.actions.add.icon').toString(),
      name: this.$i18n.t('nomenclature.Locations.newLocationLabel').toString(),
      isLeaf: true,
      parentId: parentId,
      nodeType: NodeType.VIRTUAL,
      depth: depth
    };
  }

  protected onOpen(items: string[]): void {
    this.openIds = items;
  }

  protected getDepthColorClass(depth: number): string {
    switch (depth) {
      case 0:
        return 'depth-zero';
      case 1:
        return 'depth-one';
      case 2:
        return 'depth-two';
      case 3:
        return 'depth-three';
      case 4:
        return 'depth-four';
      case 5:
        return 'depth-five';
      default:
        return 'depth-unknown';
    }
  }

  protected get NodeType(): Record<string, string> {
    return NodeType;
  }
}
